import { useSearch } from '@faststore/sdk'
import { GatsbySeo } from 'gatsby-plugin-next-seo'
import { useEffect, useRef } from 'react'
import { ButtonLink } from 'src/components/ui/Button'
import { setSessionPaginationInfo } from 'src/utils/getSessionPaginarionInfo'

import { useDelayedPagination } from './useDelayedPagination'
import { useProductsPrefetch } from './usePageProducts'

interface LinkToNextPageProps {
  totalCount: number
  setSessionPage: (pageNumber: number) => void
  sessionPathName: string
  sessionPage: number
}

const LinkToNextPage = ({
  totalCount,
  setSessionPage,
  sessionPage,
  sessionPathName,
}: LinkToNextPageProps) => {
  const { addNextPage } = useSearch()

  const { next, prev } = useDelayedPagination(totalCount)
  const bottomBoundaryRef = useRef<HTMLDivElement>(null)

  useProductsPrefetch(prev ? prev.cursor : null)
  useProductsPrefetch(next ? next.cursor : null)

  function handlePaginationOnClick() {
    const newSessionPage = sessionPage + 1

    setSessionPaginationInfo(newSessionPage, sessionPathName, window?.scrollY)
    setSessionPage(newSessionPage)
  }

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || entry.intersectionRatio <= 0) {
            return // Early return if entry is not intersecting or not visible
          }

          const { scrollY } = window
          const pageHeight = document.body.scrollHeight
          const windowHeight = window.innerHeight

          // Check if the scroll is near the bottom of the page
          if (scrollY >= pageHeight - windowHeight - 400) {
            return // Early return if scroll is near the bottom
          }

          addNextPage()
          handlePaginationOnClick()
        })
      },
      { threshold: 1 } // Trigger when the sentinel is 50% visible
    )

    const currentRef = bottomBoundaryRef.current

    if (currentRef) {
      observer.observe(currentRef)
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef)
      }
    }
  }, [bottomBoundaryRef.current, addNextPage, handlePaginationOnClick])

  return (
    <>
      {next !== false && sessionPage < 49 && (
        <div data-fs-product-listing-pagination="bottom">
          <GatsbySeo defer linkTags={[{ rel: 'next', href: next.link }]} />
          {/* Add the sentinel element */}
          <div
            ref={bottomBoundaryRef}
            style={{ height: '1px', visibility: 'hidden' }}
          />
          <ButtonLink
            className="plp__show-more-button"
            id="plp__show-more-button"
            data-testid="show-more"
            href={next.link}
            rel="next"
            variant="secondary"
            aria-label="Show more"
          >
            ver más
          </ButtonLink>
        </div>
      )}
    </>
  )
}

export default LinkToNextPage
